export const en = {
  common: {
    loading: 'Loading…',
    language: 'Language',
    english: 'English',
    ukrainian: 'Ukrainian',
    select: 'Select…',
    search: 'Search…',
    noMatches: 'No matches.',
    cancel: 'Cancel',
    close: 'Close',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    back: 'Back',
    download: 'Download',
    copied: 'Copied',
    yes: 'Yes',
    no: 'No',
  },
  auth: {
    productName: 'Article Generator',
    signInToContinue: 'Sign in to continue',
    welcomeBack: 'Welcome back',
    email: 'Email',
    password: 'Password',
    signingIn: 'Signing in…',
    signIn: 'Sign in',
  },
  roles: {
    admin: 'Administrator',
    user: 'User',
  },
  nav: {
    dashboard: 'Dashboard',
    jobs: 'Jobs',
    templates: 'Templates',
    aiKeys: 'AI keys',
    legacyImport: 'Legacy import',
    settings: 'Settings',
    allJobs: 'All jobs',
    sharedTokens: 'Shared tokens',
    users: 'Users',
    proxy: 'Proxy',
    costs: 'Costs',
    admin: 'Admin',
    closeMenu: 'Close menu',
    openMenu: 'Open menu',
    signOut: 'Sign out',
    contact: 'Questions? Contact',
  },
  theme: {
    switchToLight: 'Switch to light',
    switchToDark: 'Switch to dark',
    toggle: 'Toggle theme',
  },
  status: {
    draft: 'Draft',
    queued: 'Queued',
    running: 'Running',
    paused: 'Paused',
    succeeded: 'Succeeded',
    failed: 'Failed',
    cancelled: 'Cancelled',
    pending: 'Pending',
    generating: 'Generating',
    validating: 'Validating',
    inserting: 'Inserting',
    exporting: 'Exporting',
    regenerating: 'Regenerating',
    done: 'Done',
    review: 'Review',
    needsReview: 'Needs review',
  },
  support: {
    settings: 'Settings',
    settingsDescription: 'Configure providers, defaults, and integrations.',
    aiKeysTitle: 'AI Keys',
    aiKeysDescription: 'Manage encrypted provider keys.',
    openAiKeys: 'Open AI keys',
    templatesTitle: 'Templates',
    templatesDescription: 'Edit prompt sets used by jobs.',
    openTemplates: 'Open templates',
    notFound: 'Page not found',
    notFoundDescription: 'Page does not exist or was moved.',
    backToDashboard: 'Back to dashboard',
    legacyImport: 'Legacy import',
    legacyImportDescription: 'Paste a legacy job JSON to normalize and create a new job.',
    legacyJson: 'Legacy JSON',
    previewDiff: 'Preview diff',
    createJob: 'Create job',
    diff: 'Diff',
    noTransformations: 'No transformations needed.',
    warningsAndUnknowns: 'Warnings & unknowns',
    none: 'None.',
    unknownFieldsIgnored: 'Unknown fields ignored: {{fields}}',
    invalidJson: 'Invalid JSON',
    previewFailed: 'Preview failed',
    commitFailed: 'Commit failed',
    jobCreatedFromImport: 'Job created from legacy import',
  },
  dashboard: {
    title: 'Dashboard',
    description: 'Overview of recent jobs and quick actions.',
    totalJobs: 'Total jobs',
    readyJobs: 'Ready jobs',
    apiStatus: 'API status',
    online: 'Online',
    offline: 'Offline',
    recentJobs: 'Recent jobs',
    viewAll: 'View all',
    noJobs: 'No jobs yet',
    noJobsDescription: 'Create your first batch generation job to get started.',
    articles: '{{count}} articles',
    open: 'Open',
    spend: 'Spend',
    spendToday: 'Today',
    spendWeek: 'Last 7 days',
    spendMonth: 'This month',
    spendHint: 'OpenRouter only — other providers report no cost', spendUnavailable: 'Spend unavailable — could not reach the API',
    tasks: 'Tasks launched',
    tasksHint: 'Deleting or restarting a task does not lower the count',
  },
  adminCosts: {
    title: 'Costs',
    description: 'Spend per user, as charged by OpenRouter, and tasks launched.',
    user: 'User',
    spend: 'Spend',
    tasks: 'Tasks launched',
    today: 'Today',
    week: 'Last 7 days',
    month: 'This month',
    total: 'All users',
    deletedAccounts: 'Deleted accounts',
    empty: 'No spend recorded yet.', failed: 'Could not load costs.',
  },
  jobs: {
    title: 'Jobs',
    description: 'Browse, run, or edit batch generation jobs.',
    newJob: 'New job',
    createJob: 'Create job',
    newJobDescription: 'Configure a new batch generation job.',
    filterPlaceholder: 'Filter by name, brand, region…',
    noMatches: 'No jobs match',
    noMatchesDescription: 'Try a different search.',
    noJobs: 'No jobs yet',
    noJobsDescription: 'Create your first job to start generating articles.',
    name: 'Name',
    brand: 'Brand',
    region: 'Region',
    articles: 'Articles',
    status: 'Status',
    actions: 'Actions',
    run: 'Run',
    runStarted: 'Run started',
    runFailed: 'Run failed',
    deleteJob: 'Delete job "{{name}}"?',
    jobDeleted: 'Job deleted',
    deleteFailed: 'Delete failed',
    jobCreated: 'Job created',
    saveFailed: 'Save failed',
  },
  templates: {
    title: 'Templates',
    description: 'Reusable prompt sets used by jobs.',
    newTemplate: 'New template',
    noTemplates: 'No templates yet.',
    name: 'Name',
    systemPrompts: 'System prompts',
    version: 'Version',
    updated: 'Updated',
    titlePrompt: 'Title',
    articlePrompt: 'Article',
    custom: 'custom',
    default: 'default',
    deleteTemplate: 'Delete template "{{name}}"?',
    templateDeleted: 'Template deleted',
    templateSaved: 'Template saved',
    newTemplateTitle: 'New template',
    editTemplateTitle: 'Edit template',
    template: 'Template',
    titleGeneration: 'Title generation',
    titleGenerationDescription: 'Prompts used to generate the article title.',
    articleGeneration: 'Article generation',
    articleGenerationDescription: 'Prompts used to generate the main article body.',
    customSystemPrompt: 'Custom system prompt',
    builtInSystemPrompt: 'When off, the built-in system prompt is used.',
    userPrompt: 'User prompt',
    macros: 'Macros: {{macros}}',
    saving: 'Saving…',
  },
  aiKeys: {
    title: 'AI keys',
    description: 'Provider keys are AES-256-GCM encrypted. Plaintext is never returned.',
    addKey: 'Add key',
    noKeys: 'No keys yet.',
    lastTest: 'Last test:',
    never: 'never',
    test: 'Test',
    keyAdded: 'Key added',
    keyDeleted: 'Key deleted',
    keyUpdated: 'Key updated',
    updateFailed: 'Update failed',
    noKeySelected: 'No key selected',
    connectionOk: 'Connection OK · {{latency}}ms',
    connectionFailed: 'Connection failed',
    deleteKey: 'Delete key "{{label}}"?',
    addTitle: 'Add AI key',
    addDescription: 'Encrypted with the server\'s master key. The plaintext is only used in worker memory.',
    editTitle: 'Edit AI key',
    editDescription: 'Leave the API key field empty to keep the existing key.',
    label: 'Label',
    provider: 'Provider',
    apiKey: 'API key',
    newApiKey: 'New API key',
    optional: 'optional',
    keepCurrentKey: 'Leave empty to keep current key',
  },
  jobForm: {
    settings: 'Settings', inserts: 'Inserts', general: 'General', jobName: 'Job name', brand: 'Brand', domain: 'Domain', region: 'Region', language: 'Language', selectRegion: 'Select region…', selectLanguage: 'Select language…',
    pageInputs: 'Page inputs (keywords)', keywords: 'Keywords (one per line)', articlesToGenerate: 'Articles to generate', combination: 'Combination', oneKeyword: 'One keyword per article', allKeywords: 'All keywords', random: 'Random', availableMacros: 'Available macros: {{macros}}',
    aiWriter: 'AI writer', provider: 'Provider', model: 'Model', apiKey: 'API key', selectKey: 'Select a key', noKeys: 'No keys — add one in Settings → AI keys', sharedPool: 'Uses the shared OpenRouter pool — enter the model name above.', sendTemperature: 'Send temperature', temperature: 'Temperature', maxTokens: 'Max tokens', timeout: 'Timeout (ms)', maxPriceInput: 'Max price, input ($ per 1M tokens)', maxPriceOutput: 'Max price, output ($ per 1M tokens)', maxPriceHint: 'OpenRouter never uses a provider priced above this. If no provider fits, the article fails. Empty or 0 = no limit.', maxRunCost: 'Max cost per task ($)', maxRunCostHint: 'When a run has spent this much, it pauses (articles in progress finish). Raise the limit in the run settings to continue. Empty or 0 = no limit.', outputFormat: 'Output format', htmlLegacy: 'HTML (legacy)', jsonRecommended: 'JSON (recommended for OpenAI)',
    template: 'Template', promptTemplate: 'Prompt template', defaultTemplate: 'Default (built-in prompts)', builtInPrompts: 'Built-in defaults are used for system and user prompts.',
    export: 'Export', fileExtension: 'File extension', filenameFormat: 'Filename format', runtime: 'Runtime', concurrency: 'Concurrency', retryCount: 'Retry count', minWordCount: 'Min word count', maxWordCount: 'Max word count', minParagraphs: 'Min paragraphs', titleMaxTokens: 'Title max tokens', titleMinLength: 'Title min length', titleMaxLength: 'Title max length', titleMaxAttempts: 'Title max attempts', scheduleCron: 'Schedule cron (optional)', saving: 'Saving…',
  },
  jobEdit: {
    editJob: 'Edit job', saveAndRun: 'Save & Run', running: 'Running…', saveChanges: 'Save changes', jobSaved: 'Job saved', recentTasks: 'Recent tasks', searchTask: 'Search task…', deleteSelected: 'Delete {{count}} selected', perPage: 'Per page', noTasksMatch: 'No tasks match your search.', noTasks: 'No tasks yet.', task: 'Task', doneFailedTotal: 'Done / Failed / Total', started: 'Started', cost: 'Cost', deletingIn: 'Deleting in {{count}}s…', restartingIn: 'Restarting in {{count}}s…', undo: 'Undo', open: 'Open', pauseRun: 'Pause run', resumeRun: 'Resume run', restartRun: 'Restart run', downloadZip: 'Download ZIP', deleteRun: 'Delete run', showing: 'Showing {{from}}–{{to}} of {{total}}', previous: 'Prev', next: 'Next', pageOf: 'Page {{page}} of {{total}}', deleteTasks: 'Delete {{count}} task?', deleteTasks_plural: 'Delete {{count}} tasks?', activeRunsWarning: 'Active runs will be cancelled automatically. This action cannot be undone.', deleting: 'Deleting…', pauseFailed: 'Pause failed', resumeFailed: 'Resume failed', restartFailed: 'Restart failed', taskRestarted: 'Task restarted',
  },
  deploy: {
    button: 'Deploy', deploying: 'Deploying…', deployed: 'Deployed', deployedAt: 'Deployed {{date}}', campaign: 'Campaign {{id}}', rowHint: 'Deployed to Checker',
    connectTitle: 'Connect to Checker', connectDescription: 'Sign in with your Checker account. It is stored encrypted and used only to deploy your tasks.',
    credentialsStale: 'Your saved Checker login stopped working — sign in again to continue.',
    nameTaken: 'A campaign with this name already exists in Checker. Pick another name and deploy again.',
    nameLabel: 'Campaign name',
    retryWithName: 'Deploy with this name',
    login: 'Login', password: 'Password', connect: 'Connect', connecting: 'Connecting…', connectedAs: 'Connected as {{login}}', disconnect: 'Disconnect',
  },
  runDetail: {
    jobs: 'Jobs', job: 'Job', run: 'Run', pause: 'Pause', pinPause: 'Pin pause', pinPauseHint: 'Pin the pause — stop auto-resume from reviving this run', resume: 'Resume', cancel: 'Cancel', restart: 'Restart', restartIn: 'Restarting in {{count}}s…', downloadZip: 'Download ZIP', progress: 'Progress', done: 'done', failed: 'failed', total: 'total', live: 'Live', started: 'Started', duration: 'Duration', tokensInOut: 'Tokens in / out', cachedTokens: 'Cached tokens', cost: 'Cost', costHint: 'As charged by OpenRouter', taskSettings: 'Task settings', modified: 'Modified', articles: 'Articles', search: 'Search keyword or title…', allStatuses: 'All statuses', review: 'Review', keyword: 'Keyword', title: 'Title', words: 'Words', file: 'File', noMatches: 'No matches', noMatchesDescription: 'No articles match your filters.', noArticles: 'No articles yet', noArticlesDescription: 'Articles will appear here once generation starts.', perPage: 'Per page', deleteSelected: 'Delete {{count}} selected', retrySelected: 'Retry {{count}} selected', retryFailed: 'Retry {{count}} failed', regenerateReview: 'Regenerate {{count}} in review', queuing: 'Queuing…', reSearchVideo: 'Re-search video', regenerate: 'Regenerate', retry: 'Retry', deleteArticle: 'Delete article', deletingIn: 'Deleting in {{count}}s…', undo: 'Undo', showing: 'Showing {{from}}–{{to}} of {{total}}', previous: 'Prev', next: 'Next', pageOf: 'Page {{page}} of {{total}}', deleteArticles: 'Delete {{count}} articles?', deleteArticlesDescription: 'This will permanently remove the selected articles and update the run counters. This action cannot be undone.', deleting: 'Deleting…', regenerateReviewTitle: 'Regenerate {{count}} articles in review?', regenerateReviewDescription: 'Every article flagged for review will be regenerated from scratch — its current content will be replaced and the review flag cleared.', liveLog: 'Live log', awaitingEvents: 'Awaiting events…', noEvents: 'No events.', reviewIframe: 'Review iframe', reviewIframeHint: 'Video not inserted — add iframe manually',
  },
} as const;
